import { useState } from 'react';
import { Modal, TextInput, Stack, Button, Group, Textarea, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useAuthInfo } from '@propelauth/react';
import { env } from '../../utils/env';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationUtils';

interface CreateGroupModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  name: string;
  description: string;
  currency_code: string;
}

export const CreateGroupModal = ({ opened, onClose, onSuccess }: CreateGroupModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { accessToken } = useAuthInfo();

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      description: '',
      currency_code: 'USD',
    },
    validate: {
      name: (value) => (!value.trim() ? 'Name is required' : null),
      currency_code: (value) => (!value ? 'Currency is required' : null),
    },
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);

      const response = await fetch(`${env.API_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          name: values.name.trim(),
          description: values.description.trim() || undefined,
          currency_code: values.currency_code,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create group');
      }

      showSuccessNotification('Group created successfully');

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error creating group:', error);
      showErrorNotification(error, 'Failed to create group');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Create New Group"
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label="Group Name"
            placeholder="Enter group name"
            withAsterisk
            {...form.getInputProps('name')}
          />

          <Textarea
            label="Description"
            placeholder="Optional description"
            {...form.getInputProps('description')}
          />

          <Select
            label="Currency"
            withAsterisk
            {...form.getInputProps('currency_code')}
            data={[
              { value: 'USD', label: 'USD - US Dollar' },
              { value: 'EUR', label: 'EUR - Euro' },
              { value: 'GBP', label: 'GBP - British Pound' },
              { value: 'CAD', label: 'CAD - Canadian Dollar' },
              { value: 'AUD', label: 'AUD - Australian Dollar' },
            ]}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Create Group
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}; 